import React, { ReactElement } from 'react';

// Hooks
import {
    useActiveTiptapEditorTouchCallback,
    useTiptapActiveEditorSelector,
} from '../../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Utils
import {
    isListTypeClosestActive,
    hasNestedListContent,
} from '../../../../../../common/tiptap/extensions/list/tiptapListUtils';
import { Editor, findParentNode } from '@tiptap/react';

// Components
import RichTextTool from './RichTextTool';

// Types
import { ToolbarToolProps } from '../../../config/toolDefinitions/toolbarToolTypes';
import { TiptapNodeType } from '../../../../../../common/tiptap/tiptapTypes';

type IndentCommand = 'sinkListItem' | 'liftListItem';

interface TiptapIndentControlProps extends ToolbarToolProps {
    command: IndentCommand;
}

const runIndentCommand = (editor: Editor, command: IndentCommand) => {
    if (command === 'sinkListItem') return editor.commands?.[command]?.('listItem');

    // liftListItemWithNestedContent only handles nested lists; fall back to the standard command otherwise.
    const listItem = findParentNode((node) => node.type.name === TiptapNodeType.listItem)(editor.state.selection);

    return listItem && hasNestedListContent(listItem.node)
        ? editor.commands.liftListItemWithNestedContent()
        : editor.commands?.[command]?.('listItem');
};

const TiptapTextIndentControlTool = ({ command, ...props }: TiptapIndentControlProps): ReactElement => {
    const isOrderListActive = useTiptapActiveEditorSelector(isListTypeClosestActive(TiptapNodeType.orderedList));
    const isBulletListActive = useTiptapActiveEditorSelector(isListTypeClosestActive(TiptapNodeType.bulletList));

    const isListActive = isOrderListActive || isBulletListActive;

    const canExecute = useTiptapActiveEditorSelector(
        (editor) => (isListActive && editor?.can()?.[command]?.('listItem')) || false,
    );

    const handleClick = useActiveTiptapEditorTouchCallback(
        (editor, event) => {
            event?.preventDefault();
            runIndentCommand(editor, command);
        },
        [command],
    );

    return (
        <RichTextTool
            onClick={handleClick}
            iconName={props.iconName || 'toolbar-indent'}
            disabled={!canExecute}
            {...props}
        />
    );
};

export default TiptapTextIndentControlTool;
